// Rewarded-ad hint economy (§ ads): up to 3 ad-hints per calendar day, tracked
// in localStorage. A "full hint" reveals the complete recipe of an element the
// player can forge right now but hasn't discovered yet.
import { ELEMENT_DEFS } from "../components/elementDefinitions";

export const MAX_AD_HINTS_PER_DAY = 3;
const KEY = "mc_adhints";

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

interface Store {
  date: string;
  count: number;
}

const read = (now: Date): Store => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null") as Store | null;
    if (raw && raw.date === dayKey(now)) return raw;
  } catch {
    /* ignore */
  }
  return { date: dayKey(now), count: 0 };
};

export interface AdHintState {
  used: number;
  remaining: number;
  max: number;
}

export const getAdHintState = (now = new Date()): AdHintState => {
  const s = read(now);
  return { used: s.count, remaining: Math.max(0, MAX_AD_HINTS_PER_DAY - s.count), max: MAX_AD_HINTS_PER_DAY };
};

/** Record one used ad-hint. Returns the updated state (no-op if already capped). */
export const consumeAdHint = (now = new Date()): AdHintState => {
  const s = read(now);
  if (s.count < MAX_AD_HINTS_PER_DAY) {
    s.count += 1;
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore */ }
  }
  return getAdHintState(now);
};

export interface RecipeHint {
  resultId: string;
  resultName: string;
  ingredients: string[];
  ingredientNames: string[];
}

/**
 * Find a "next" recipe the player can make right now: an undiscovered element
 * whose ingredients are ALL already unlocked. Prefers the lowest tier (most
 * approachable), breaking ties deterministically by name.
 */
export const getNextRecipeHint = (unlocked: Set<string>): RecipeHint | null => {
  const candidates = Object.values(ELEMENT_DEFS).filter(
    (e) =>
      e.recipe.length > 0 &&
      !unlocked.has(e.id) &&
      e.recipe.every((ing) => unlocked.has(ing))
  );
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => (a.tier - b.tier) || a.name.localeCompare(b.name));
  const pick = candidates[0];
  return {
    resultId: pick.id,
    resultName: pick.name,
    ingredients: pick.recipe,
    ingredientNames: pick.recipe.map((i) => ELEMENT_DEFS[i]?.name ?? i),
  };
};
